import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button as MuiButton,
  Chip,
  Divider,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import ShipmentTimelineCard from '../components/orders/ShipmentTimelineCard';
import { ROUTES } from '../constants';
import { useAppDispatch, useAppSelector } from '../hooks';
import { cancelOrderById, fetchOrderById } from '../features/ordersSlice';
import { addToCart, fetchMyCart } from '../features/cartSlice';
import { formatCurrency } from '../utils/pricing';

const ORDER_STEPS = ['pending', 'processing', 'shipped', 'delivered'] as const;

const statusToStepIndex = (status: string): number => {
  const index = ORDER_STEPS.indexOf(status as (typeof ORDER_STEPS)[number]);
  return index >= 0 ? index : 0;
};

const formatStatusLabel = (status: string) => status.charAt(0).toUpperCase() + status.slice(1);

const OrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const { selectedOrder, loading, cancelling, error } = useAppSelector((state) => state.orders);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate(ROUTES.LOGIN);
      return;
    }
    if (id) {
      dispatch(fetchOrderById(id));
    }
  }, [dispatch, id, navigate, token]);

  const canCancel = useMemo(
    () =>
      Boolean(
        selectedOrder &&
          (selectedOrder.status === 'pending' || selectedOrder.status === 'processing')
      ),
    [selectedOrder]
  );

  const onCancelOrder = async () => {
    if (!selectedOrder) return;
    setActionMessage(null);
    setActionError(null);
    const result = await dispatch(cancelOrderById(selectedOrder._id));
    if (cancelOrderById.fulfilled.match(result)) {
      setActionMessage('Order cancelled successfully.');
      return;
    }
    setActionError(result.payload || 'Failed to cancel order');
  };

  const onReorder = async () => {
    if (!selectedOrder) return;
    setReordering(true);
    setActionMessage(null);
    setActionError(null);

    try {
      for (const item of selectedOrder.items) {
        await dispatch(
          addToCart({
            productId: item.product._id,
            quantity: item.quantity
          })
        ).unwrap();
      }
      await dispatch(fetchMyCart());
      setActionMessage('Items from this order were added to cart.');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setReordering(false);
    }
  };

  if (!token) return null;

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Order details</Typography>
        <MuiButton variant="text" component={RouterLink} to={ROUTES.ACCOUNT}>
          Back to account
        </MuiButton>
      </Box>

      {(error || actionError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError || error}
        </Alert>
      )}
      {actionMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {actionMessage}
        </Alert>
      )}

      {loading && <Typography>Loading order...</Typography>}

      {!loading && selectedOrder && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1
            }}
          >
            <Box>
              <Typography variant="h6">Order #{selectedOrder._id.slice(-8).toUpperCase()}</Typography>
              <Typography variant="body2" color="text.secondary">
                Placed on{' '}
                {selectedOrder.createdAt
                  ? new Date(selectedOrder.createdAt).toLocaleString()
                  : '-'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`Payment: ${formatStatusLabel(selectedOrder.paymentStatus)}`}
                color={selectedOrder.paymentStatus === 'paid' ? 'success' : 'default'}
                variant="outlined"
              />
              <Chip
                size="small"
                label={formatStatusLabel(selectedOrder.status)}
                color={
                  selectedOrder.status === 'delivered'
                    ? 'success'
                    : selectedOrder.status === 'cancelled'
                      ? 'error'
                      : 'primary'
                }
              />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Shipping address
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedOrder.customerName}, {selectedOrder.customerPhone}
            <br />
            {selectedOrder.shippingAddress.street}, {selectedOrder.shippingAddress.city},{' '}
            {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.zipCode},{' '}
            {selectedOrder.shippingAddress.country}
          </Typography>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Order status timeline
          </Typography>
          {selectedOrder.status === 'cancelled' ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This order has been cancelled.
            </Alert>
          ) : (
            <Stepper
              activeStep={statusToStepIndex(selectedOrder.status)}
              alternativeLabel
              sx={{ mb: 2, pt: 0.5 }}
            >
              {ORDER_STEPS.map((step) => (
                <Step key={step}>
                  <StepLabel>{formatStatusLabel(step)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Items
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {selectedOrder.items.map((item) => (
              <Box
                key={`${selectedOrder._id}-${item.product._id}`}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {item.product.name} x{item.quantity}
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(item.price * item.quantity)}
                </Typography>
              </Box>
            ))}
          </Stack>

          {selectedOrder.shipment?.waybill && (
            <ShipmentTimelineCard
              orderId={selectedOrder._id}
              waybill={selectedOrder.shipment.waybill}
              lastScan={selectedOrder.shipment.lastScan}
            />
          )}
          <Divider sx={{ mb: 2 }} />
          {Boolean(selectedOrder.discountAmount) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="success.main">
                Discount{selectedOrder.couponCode ? ` (${selectedOrder.couponCode})` : ''}
              </Typography>
              <Typography variant="body2" color="success.main">
                -{formatCurrency(selectedOrder.discountAmount || 0)}
              </Typography>
            </Box>
          )}
          {Boolean(selectedOrder.paymentDiscount) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="success.main">
                Online payment discount
              </Typography>
              <Typography variant="body2" color="success.main">
                -{formatCurrency(selectedOrder.paymentDiscount || 0)}
              </Typography>
            </Box>
          )}
          {Boolean(selectedOrder.shippingCharge) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Delivery
              </Typography>
              <Typography variant="body2">
                {formatCurrency(selectedOrder.shippingCharge || 0)}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1">Total</Typography>
            <Typography variant="subtitle1">{formatCurrency(selectedOrder.totalAmount)}</Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <MuiButton variant="contained" onClick={onReorder} disabled={reordering}>
              {reordering ? 'Adding to cart...' : 'Reorder'}
            </MuiButton>
            {canCancel && (
              <MuiButton
                variant="outlined"
                color="error"
                onClick={onCancelOrder}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel order'}
              </MuiButton>
            )}
            <MuiButton variant="text" component={RouterLink} to={ROUTES.CART}>
              Go to cart
            </MuiButton>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default OrderDetailsPage;
